<template>
  <Main>
    <ToolsPanel
      :breadCrumbFirst="$t('integrationResponses.breadcrumb')"
      :title="$t('integrationResponses.title')"
    >
      <div class="list__buttons"></div>
    </ToolsPanel>
    <ContentContainer>
      <div class="list__filter">
        <div class="list__filter-item list__filter-item--presets">
          <DateRangePresets :selected="activeDatePreset" @select="applyDatePreset" />
        </div>

        <div class="list__filter-item">
          <b>{{ $t("integrationResponses.from") }}</b>
          <DatePicker v-model="filterData.date_from" :format="'DD.MM.YYYY'" />
        </div>
        <div class="list__filter-item">
          <b>{{ $t("integrationResponses.to") }}</b>
          <DatePicker v-model="filterData.date_to" :format="'DD.MM.YYYY'" />
        </div>

        <div class="list__filter-item">
          <MainSelect
            :id="'class_option'"
            :placeholder="$t('integrationResponses.classPlaceholder')"
            :option-list="classList"
            :set-value="filterData"
            :value="filterData.class_option"
            :labelName="'name'"
          />
        </div>

        <div class="list__filter-item">
          <MainSelect
            :id="'integration'"
            :placeholder="$t('integrationResponses.integrationPlaceholder')"
            :option-list="integrationList"
            :set-value="filterData"
            :value="filterData.integration"
            :labelName="'name'"
            :searchable="true"
          />
        </div>

        <div class="list__filter-item list__filter-item--checkbox">
          <label>
            <input type="checkbox" v-model="filterData.show_resolved" />
            {{ $t("integrationResponses.showResolved") }}
          </label>
        </div>

        <div class="list__filter-button">
          <MainButton @click.native="filterList()">
            {{ $t("integrationResponses.filter") }}
          </MainButton>
        </div>
        <div class="list__filter-button">
          <MainButton @click.native="filterListReset()">
            {{ $t("integrationResponses.reset") }}
          </MainButton>
        </div>
      </div>

      <div class="list">
        <div v-if="rows.length" class="responses">
          <div class="responses__header">
            <div class="responses__cell">{{ $t("integrationResponses.colIntegration") }}</div>
            <div class="responses__cell">{{ $t("integrationResponses.colClass") }}</div>
            <div class="responses__cell responses__cell--body">
              {{ $t("integrationResponses.colBody") }}
            </div>
            <div class="responses__cell responses__cell--count">
              {{ $t("integrationResponses.colCount") }}
            </div>
            <div class="responses__cell">{{ $t("integrationResponses.colSeen") }}</div>
            <div class="responses__cell">{{ $t("integrationResponses.colOutcome") }}</div>
            <div class="responses__cell responses__cell--leads">
              {{ $t("integrationResponses.colLeads") }}
            </div>
            <div class="responses__cell responses__cell--action"></div>
          </div>

          <div v-for="row in rows" :key="row.group_key" class="responses__row">
            <div class="responses__cell">
              <div class="responses__integration">{{ integrationName(row.integration_id) }}</div>
              <div class="responses__muted">#{{ row.integration_id }}</div>
            </div>

            <div class="responses__cell">
              <span :class="['responses__class', 'responses__class--' + row.class]">
                {{ classLabel(row.class) }}
              </span>
            </div>

            <div class="responses__cell responses__cell--body">
              <div
                v-if="!row.body"
                class="responses__muted"
              >
                {{ $t("integrationResponses.emptyBody") }}
              </div>
              <div v-else class="responses__body" @click="toggleBody(row.group_key)">
                {{ isBodyOpen(row.group_key) ? row.body : shortBody(row.body) }}
              </div>
              <div
                v-if="isLongBody(row.body)"
                class="responses__expand"
                @click="toggleBody(row.group_key)"
              >
                {{
                  isBodyOpen(row.group_key)
                    ? $t("integrationResponses.collapse")
                    : $t("integrationResponses.expand")
                }}
              </div>
            </div>

            <div class="responses__cell responses__cell--count">
              <b>{{ row.count }}</b>
            </div>

            <div class="responses__cell">
              <div class="responses__muted">{{ formatDate(row.first_at) }}</div>
              <div>{{ formatDate(row.last_at) }}</div>
            </div>

            <div class="responses__cell">{{ outcomeLabel(row.outcome) }}</div>

            <div class="responses__cell responses__cell--leads">
              <span v-for="leadId in row.lead_ids" :key="leadId" class="responses__lead">
                <router-link :to="leadLink(leadId)" tag="a">{{ leadId }}</router-link>
              </span>
              <span v-if="hiddenLeads(row)" class="responses__muted">
                {{ $t("integrationResponses.moreLeads", { count: hiddenLeads(row) }) }}
              </span>
            </div>

            <div class="responses__cell responses__cell--action">
              <div v-if="row.resolved_at" class="responses__muted">
                {{ $t("integrationResponses.resolvedAt", { date: formatDate(row.resolved_at) }) }}
              </div>
              <MainButton v-else @click.native="resolveRow(row)">
                {{ $t("integrationResponses.resolve") }}
              </MainButton>
            </div>
          </div>
        </div>
        <div class="list__no-have" v-else>{{ $t("integrationResponses.empty") }}</div>
      </div>
    </ContentContainer>

    <FetchSpinnerModal v-if="loading" />
  </Main>
</template>

<script>
import ROUTE from "~/constants/routes";
import leadApi from "~/api/lead";
import moment from "moment";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { getStoredUserInfo } from "~/helpers/userAuth";

import Main from "~/templates/Main.vue";
import FetchSpinnerModal from "~/components/molecules/FetchSpinnerModal.vue";
import ToolsPanel from "~/components/molecules/ToolsPanel.vue";
import ContentContainer from "~/components/molecules/ContentContainer.vue";
import MainButton from "~/components/atoms/buttons/MainButton.vue";
import MainSelect from "~/components/atoms/inputs/MainSelect.vue";
import DateRangePresets from "~/components/molecules/DateRangePresets.vue";

const BODY_LIMIT = 160;

/**
 * Задача 11: ответы партнёров на отправку лида, которые тул не распознал.
 *
 * Строки приходят уже сгруппированными: одинаковый ответ одной интеграции —
 * одна строка со счётчиком. Двенадцать раз «Track map is not available» — это
 * один инцидент с партнёром, а не двенадцать алертов, иначе раздел забьётся
 * за день и его перестанут открывать.
 *
 * Кнопки «отправить заново» здесь нет намеренно: если партнёр лида всё-таки
 * создал, повтор сделает дубль и двойную продажу. Решение — за человеком,
 * который видит текст ответа.
 */
export default {
  name: "IntegrationResponsesList",
  metaInfo() {
    return {
      title: this.$t("integrationResponses.metaTitle"),
    };
  },
  data() {
    return {
      loading: false,
      rows: [],
      integrationList: [],
      openBodies: [],
      activeDatePreset: "",

      // класс по умолчанию — нераспознанные: именно они требуют реакции
      filterData: {
        date_from: null,
        date_to: null,
        class_option: { id: "unknown", name: "" },
        integration: null,
        show_resolved: false,
      },
    };
  },
  components: {
    Main,
    FetchSpinnerModal,
    ToolsPanel,
    ContentContainer,
    MainButton,
    MainSelect,
    DatePicker,
    DateRangePresets,
  },
  computed: {
    classList() {
      return ["unknown", "not_accepting", "duplicate", "invalid_email", "retry_later", "success"].map(
        (id) => ({ id, name: this.classLabel(id) }),
      );
    },
  },
  mounted() {
    // страница только для админа: в этом приложении роль берётся из группы
    // пользователя так же, как в меню
    const user = getStoredUserInfo();
    const groupId = user && user.groups && user.groups.length ? user.groups[0].id : null;

    if (groupId !== 2) {
      this.$router.replace(ROUTE.HOME_PAGE);
      return;
    }

    this.filterData.class_option = { id: "unknown", name: this.classLabel("unknown") };

    this.getIntegrations();
    this.getList();
  },
  methods: {
    formatDate(date) {
      if (!date) {
        return "";
      }

      return moment(date).format("DD.MM.YYYY HH:mm");
    },
    classLabel(code) {
      return this.$t("integrationResponses.classes." + code);
    },
    outcomeLabel(code) {
      if (!code) {
        return "";
      }

      const key = "integrationResponses.outcomes." + code;
      const label = this.$t(key);

      return label === key ? code : label;
    },
    integrationName(id) {
      const found = this.integrationList.find((item) => item.id === id);

      return found ? found.name : "—";
    },
    leadLink(leadId) {
      return ROUTE.LEAD_LIST.replace(":page", 1) + "?id=" + leadId;
    },
    hiddenLeads(row) {
      const shown = row.lead_ids ? row.lead_ids.length : 0;
      const total = row.lead_ids_total || shown;

      return total > shown ? total - shown : 0;
    },
    isLongBody(body) {
      return !!body && body.length > BODY_LIMIT;
    },
    shortBody(body) {
      if (!body) {
        return "";
      }

      return this.isLongBody(body) ? body.slice(0, BODY_LIMIT) + "…" : body;
    },
    isBodyOpen(key) {
      return this.openBodies.includes(key);
    },
    toggleBody(key) {
      if (this.isBodyOpen(key)) {
        this.openBodies = this.openBodies.filter((item) => item !== key);
      } else {
        this.openBodies = this.openBodies.concat([key]);
      }
    },
    applyDatePreset({ key, from, to }) {
      this.activeDatePreset = key;
      this.filterData.date_from = from;
      this.filterData.date_to = to;
      this.getList();
    },
    filterList() {
      this.getList();
    },
    filterListReset() {
      this.activeDatePreset = "";
      this.filterData.date_from = null;
      this.filterData.date_to = null;
      this.filterData.class_option = { id: "unknown", name: this.classLabel("unknown") };
      this.filterData.integration = null;
      this.filterData.show_resolved = false;

      this.getList();
    },
    async getIntegrations() {
      try {
        const response = await leadApi.getIntegrationAll();

        this.integrationList = response.data || [];
      } catch (error) {
        this.integrationList = [];
      }
    },
    async getList() {
      this.loading = true;

      const data = {
        class: this.filterData.class_option ? this.filterData.class_option.id : "unknown",
        resolved: this.filterData.show_resolved,
      };

      if (this.filterData.date_from) {
        data.date_from = moment(this.filterData.date_from).format("YYYY-MM-DD HH:mm:ss");
      }

      if (this.filterData.date_to) {
        data.date_to = moment(this.filterData.date_to).format("YYYY-MM-DD HH:mm:ss");
      }

      if (this.filterData.integration) {
        data.integration_id = this.filterData.integration.id;
      }

      try {
        const response = await leadApi.getIntegrationResponses(data);

        this.rows = response.data || [];
        this.openBodies = [];
      } catch (error) {
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    async resolveRow(row) {
      this.loading = true;

      try {
        await leadApi.resolveIntegrationResponses({
          group_key: row.group_key,
          integration_id: row.integration_id,
        });

        // разобранная группа уходит из списка неразобранных сразу, не ждём перезапроса
        if (!this.filterData.show_resolved) {
          this.rows = this.rows.filter((item) => item.group_key !== row.group_key);
        } else {
          row.resolved_at = new Date().toISOString();
        }

        // бейдж в меню считает неразобранные — пересчитать сразу после пометки
        this.$root.$emit("integration-responses-unread-refresh");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.list {
  &__filter {
    padding: 10px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__buttons {
    display: flex;
  }

  &__filter-item {
    width: 170px;
    margin-right: 10px;
    padding-bottom: 10px;
    display: flex;
    align-items: center;

    &--presets {
      width: auto;
      max-width: 100%;
    }

    &--checkbox {
      width: auto;

      label {
        display: flex;
        align-items: center;
        font-size: 14px;
        cursor: pointer;
      }

      input {
        width: auto;
        height: auto;
        margin-right: 6px;
      }
    }

    input {
      padding: 5px 10px;
      font-size: 14px;
      width: 100%;
      height: 40px;
    }
  }

  &__filter-button {
    width: 100px;
    margin-right: 10px;
    padding-bottom: 10px;
  }
}

// на узких экранах таблицу скроллим целиком, иначе колонки сплющиваются
.list {
  overflow-x: auto;
}

.responses {
  font-size: 13px;
  min-width: 1120px;

  &__header,
  &__row {
    display: grid;
    grid-template-columns: 1.3fr 0.9fr 2.4fr 0.4fr 1fr 0.9fr 1.4fr 1.3fr;
    gap: 10px;
    align-items: start;
    padding: 10px 6px;
    border-bottom: 1px solid $mainDisabled;
  }

  &__header {
    font-weight: bold;
    border-bottom: 2px solid $mainDisabled;
  }

  &__cell {
    overflow-wrap: anywhere;

    &--count {
      text-align: center;
    }

    &--action {
      white-space: nowrap;
    }
  }

  &__integration {
    font-weight: bold;
  }

  &__muted {
    color: $mainSecondTextColor;
    font-size: 12px;
  }

  &__body {
    font-family: monospace;
    cursor: pointer;
    white-space: pre-wrap;
  }

  &__expand {
    padding-top: 4px;
    font-size: 12px;
    text-decoration: underline;
    cursor: pointer;
  }

  &__class {
    display: inline-block;
    padding: 3px 7px;
    border-radius: 4px;
    background: $mainDisabled;
    white-space: nowrap;

    &--unknown {
      background: #c62828;
      color: #fff;
    }

    &--retry_later {
      background: #ef6c00;
      color: #fff;
    }
  }

  &__lead {
    margin-right: 6px;

    a {
      text-decoration: underline;
    }
  }
}
</style>
